//lib/queryClient.ts
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAccessToken, reactiveRefresh, clearAuthState, getOrCreateDeviceId } from "./authToken";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(method: string, url: string, data?: unknown): Promise<Response> {
  let token = getAccessToken();

  // Normalize common collection endpoint without trailing slash to avoid 307 redirect
  if (url === "/api/signals") url = "/api/signals/";

  const fullUrl = url.startsWith("http")
    ? url
    : `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

  const headers: Record<string, string> = {};
  if (data && !(data instanceof FormData)) headers["Content-Type"] = "application/json";
  // Always explicitly request JSON to reduce chances a proxy returns HTML shell
  headers["Accept"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try { headers['X-Device-Id'] = getOrCreateDeviceId(); } catch { /* ignore */ }

  let res = await fetch(fullUrl, {
    method,
    headers,
    credentials: 'include',
    body: data ? (data instanceof FormData ? (data as any) : JSON.stringify(data)) : undefined,
  });

  // If 401 and we had a token -> try refresh once then retry
  if (res.status === 401 && token) {
    token = await reactiveRefresh();
    if (token) {
      if (data && !(data instanceof FormData)) headers["Content-Type"] = "application/json";
      headers["Authorization"] = `Bearer ${token}`;
      res = await fetch(fullUrl, {
        method,
        headers,
        credentials: 'include',
        body: data ? (data instanceof FormData ? (data as any) : JSON.stringify(data)) : undefined,
      });
    } else {
      // Strict: refresh failed -> clear auth so app can redirect to login
      clearAuthState();
    }
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn =
  ({ on401 }: { on401: UnauthorizedBehavior }): QueryFunction<unknown> =>
  async ({ queryKey }) => {
    let path = queryKey[0] as string;
    if (path === "/api/signals") path = "/api/signals/"; // normalize
    const url = `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  let token = getAccessToken();

  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  try { headers['X-Device-Id'] = getOrCreateDeviceId(); } catch { /* ignore */ }
  let res = await fetch(url, { headers, credentials: 'include' });

    // handle 401 specially if requested
    if (res.status === 401 && token) {
      token = await reactiveRefresh();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        res = await fetch(url, { headers, credentials: 'include' });
      } else {
        // Strict: refresh failed -> clear auth
        clearAuthState();
      }
    }

    if (on401 === "returnNull" && res.status === 401) {
      clearAuthState();
      return null;
    }

    // read body as text first so we can safely inspect content-type / show clearer errors
    const text = await res.text();
    const ct = (res.headers.get("content-type") || "").toLowerCase();

    // Non-OK responses: include body snippet for debugging
    if (!res.ok) {
      const bodySnippet = text ? ` - ${text.slice(0, 500)}` : "";
      throw new Error(`${res.status}: ${res.statusText}${bodySnippet}`);
    }

    // If response is not JSON, throw a descriptive error instead of letting JSON.parse blow up
    if (!ct.includes("application/json") && !ct.includes("+json")) {
      const preview = text ? text.slice(0, 500) : "";
      throw new Error(`Expected JSON response but got '${ct || "unknown"}': ${preview}`);
    }

    try {
      return JSON.parse(text);
    } catch (e: any) {
      const preview = text ? text.slice(0, 1000) : "";
      throw new Error(`Invalid JSON response: ${e?.message || e} - ${preview}`);
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      // Always-fresh policy
      staleTime: 0,
      // Keep cached data briefly so route changes/offline don't yield blank UI
      gcTime: 5 * 60 * 1000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: false,
      retry: false,
    },
    mutations: { retry: false },
  },
});
